/**
 * Lookup Component
 * Allows users to enter a single question ID for preview
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useResources } from "../../hooks/useResources";
import { validateQuestionId } from "../../helpers/csvParser";
import "./Lookup.scss";

const Lookup = () => {
  const [questionId, setQuestionId] = useState("");
  const [error, setError] = useState<string | null>(null);
  
  const navigate = useNavigate();
  const { getResourceValue } = useResources();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    setQuestionId(value);
    
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const handlePreviewQuestions = () => {
    if (!questionId) {
      setError(getResourceValue("please_enter_question_id", "Please enter a question ID"));
      return;
    }

    if (!validateQuestionId(questionId)) {
      setError(
        getResourceValue(
          "invalid_question_id_format",
          "Invalid question ID format. Question ID must be exactly 12 digits."
        )
      );
      return;
    }

    // Navigate with question ID in URL
    navigate(`/preview-questions/results/${questionId}`);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handlePreviewQuestions();
    }
  };

  const isValidId = questionId && validateQuestionId(questionId);

  return (
    <div className="manual-id-input">
      <div className="manual-id-input__content">
        <h2 className="manual-id-input__title">
          {getResourceValue("preview_questions", "Preview Questions")}
        </h2>

        <div className="input-container">
          <input
            type="text"
            className="question-id-input"
            placeholder={getResourceValue("enter_question_id", "Enter Question ID")}
            value={questionId}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            maxLength={12}
            autoFocus
          />
          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}
        </div>

        <button
          className="btn btn-green preview-btn"
          onClick={handlePreviewQuestions}
          disabled={!isValidId}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            style={{ marginRight: "8px" }}
          >
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
          {getResourceValue("preview_questions", "Preview Questions")}
        </button>
      </div>
    </div>
  );
};

export default Lookup;
